import { Autocomplete, TextField } from "@mui/material";
import GenericModal from "../GenericModal/GenericModal";
import { InviteUserResponse } from "./hooks/useInviteUser";
import { UpdateOrgCompanyDescriptionResponse } from "./hooks/useUpdateOrgCompanyDescription";
import { UpdateOrgNameResponse } from "./hooks/useUpdateOrgName";
import { UpdateUserDataResponse } from "./hooks/useUpdateUserData";

const settingsModalWidth = "min(600px, calc(100vw - 32px))";

export default function Modals({
  useUpdateOrgName,
  useUpdateOrgCompanyDescription,
  useInviteUser,
  useUpdateUserData,
}: {
  useUpdateOrgName: UpdateOrgNameResponse;
  useUpdateOrgCompanyDescription: UpdateOrgCompanyDescriptionResponse;
  useInviteUser: InviteUserResponse;
  useUpdateUserData: UpdateUserDataResponse;
}) {
  const {
    setOrgName,
    isOrgNameFilled,
    handleCloseChangeOrgNameModal,
    openChangeOrgNameModal,
    handleUpdateOrgName,
    oldName,
  } = useUpdateOrgName;

  const {
    setCompanyDescription,
    isCompanyDescriptionDirty,
    handleCloseChangeOrgCompanyDescriptionModal,
    openChangeOrgCompanyDescriptionModal,
    handleUpdateOrgCompanyDescription,
    oldCompanyDescription,
  } = useUpdateOrgCompanyDescription;

  const {
    setOpenInviteUserModal,
    invitedUser,
    setInvitedUser,
    areAllInviteUserInputsFilled,
    handleInviteUser,
    openInviteUserModal,
  } = useInviteUser;

  const {
    selectedUser,
    userToUpdate,
    setUserToUpdate,
    areAllUpdateUserInputsFilled,
    openUpdateUserModal,
    handleUpdateUser,
    handleCloseUpdateUserModal,
    selectedFunctionalGroupIds,
    setSelectedFunctionalGroupIds,
    editableFunctionalGroups,
    isUpdatingUser,
  } = useUpdateUserData;

  const genericModalDictionary = {
    changeOrgName: {
      title: "Organization Name",
      description: "Change the name of your organization",
      width: settingsModalWidth,
      isDisabled: !isOrgNameFilled,
      handleSave: handleUpdateOrgName,
      handleCancel: handleCloseChangeOrgNameModal,
      isOpen: openChangeOrgNameModal,
      children: (
        <TextField
          label="Name"
          size="small"
          fullWidth
          defaultValue={oldName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setOrgName(event.target.value)
          }
          style={{ padding: 0 }}
        />
      ),
    },
    changeOrgCompanyDescription: {
      title: "Company Description",
      width: settingsModalWidth,
      isDisabled: !isCompanyDescriptionDirty,
      handleSave: handleUpdateOrgCompanyDescription,
      handleCancel: handleCloseChangeOrgCompanyDescriptionModal,
      isOpen: openChangeOrgCompanyDescriptionModal,
      children: (
        <TextField
          label="Company Description"
          fullWidth
          multiline
          rows={8}
          defaultValue={oldCompanyDescription}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCompanyDescription(event.target.value)
          }
          style={{ padding: 0 }}
        />
      ),
    },
    inviteUser: {
      title: "Invite User",
      description: "Invite a new user to your organization",
      isDisabled: !areAllInviteUserInputsFilled,
      handleSave: handleInviteUser,
      handleCancel: () => setOpenInviteUserModal(false),
      isOpen: openInviteUserModal,
      children: (
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="First Name"
            size="small"
            fullWidth
            value={invitedUser.firstName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setInvitedUser({
                ...invitedUser,
                firstName: event.target.value,
              })
            }
            style={{ padding: 0 }}
          />
          <TextField
            label="Last Name"
            size="small"
            fullWidth
            value={invitedUser.lastName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setInvitedUser({
                ...invitedUser,
                lastName: event.target.value,
              })
            }
          />
          <TextField
            label="Email"
            size="small"
            fullWidth
            className="col-span-full"
            value={invitedUser.email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setInvitedUser({
                ...invitedUser,
                email: event.target.value,
              })
            }
          />
        </div>
      ),
    },
    updateUser: {
      title: "Update User",
      description: `Update ${selectedUser?.firstName ? selectedUser.firstName : "user"}'s information`,
      isDisabled: !areAllUpdateUserInputsFilled,
      isLoading: isUpdatingUser,
      handleSave: () => handleUpdateUser({ userId: selectedUser?.id || "" }),
      handleCancel: handleCloseUpdateUserModal,
      isOpen: openUpdateUserModal,
      children: (
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="First Name"
            size="small"
            fullWidth
            value={userToUpdate.firstName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setUserToUpdate({
                lastName: userToUpdate.lastName,
                firstName: event.target.value,
              });
            }}
            style={{ padding: 0 }}
          />
          <TextField
            label="Last Name"
            size="small"
            fullWidth
            value={userToUpdate.lastName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setUserToUpdate({
                firstName: userToUpdate.firstName,
                lastName: event.target.value,
              })
            }
          />
          {editableFunctionalGroups.length > 0 && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={editableFunctionalGroups}
              value={editableFunctionalGroups.filter((group) =>
                selectedFunctionalGroupIds.includes(group.id)
              )}
              onChange={(_, value) => {
                setSelectedFunctionalGroupIds(value.map((group) => group.id));
              }}
              getOptionLabel={(option) =>
                `${option.name} (${option.acronym})`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Functional Groups"
                  size="small"
                />
              )}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  paddingTop: "14px !important",
                  paddingBottom: "8px !important",
                  paddingLeft: "8px !important",
                },
                "& .MuiAutocomplete-tag": {
                  marginTop: "8px",
                },
              }}
              className="col-span-full"
            />
          )}
        </div>
      ),
    },
  };

  const openModal = Object.values(genericModalDictionary).filter(
    (modal) => modal.isOpen
  );

  return <GenericModal {...openModal[0]} />;
}
